import {
  Box,
  Button,
  InputLabel,
  SelectChangeEvent,
  Stack,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { FC, useState } from "react";
import { labelSty } from "../UIElements/styles-input";
import useResponsive from "../../hooks/useResponsive";

import { JobEmployee, JobEmployeeProps } from "../../interface/job_board";
import {
  InputCustomDisableSize,
  InputCustomSize,
  TextArea,
} from "../UIElements/input-ui";
import dayjs, { Dayjs } from "dayjs";
import CustomDatePicker from "../UIElements/date-picker";
import { LoadingButton } from "@mui/lab";
import { useMutation } from "@tanstack/react-query";
import { useNotificationService } from "../notification";
import { apiCreateEmployee } from "../../api/employment";
import { PATH_EMPLOYMENT } from "../../routes/path";
import { useNavigate } from "react-router-dom";
import { ApplicantDetails } from "../../interface/application";
import { extractContractDates, formatTextType } from "../../utils/url-id";
import { validateEmploeeApplication } from "../../utils/validations";
import SelectInputMultiple from "../UIElements/select-multiple";

const EmployeeForm: FC<{
  candidate_info: ApplicantDetails;
  onCancel?: () => void;
}> = ({ candidate_info, onCancel }) => {
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xl");
  const { showNotification } = useNotificationService();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState<{
    start_date: Dayjs | null;
    end_date: Dayjs | null;
  }>({
    start_date: null,
    end_date: null,
  });

  const [employeeData, setEmployeeData] = useState<JobEmployee>({
    application: candidate_info?._id,
    position: candidate_info?.job_info?.job_title,
    salary: "",
    work_type: candidate_info?.job_info?.work_type,
    employment_type: candidate_info?.job_info?.employment_type,
    contract_duration:
      extractContractDates(candidate_info?.job_info?.contract_duration) ?? "-",
    note: "",
  });
  const [contractDuration, setContractDuration] = useState<any>({
    startDate: new Date().toISOString(),
    endDate: new Date().toISOString(),
  });
  const [benefits, setBenefits] = useState<string[]>([]);

  const handleStartDateChange = (newValue: Dayjs | null) => {
    if (newValue?.isBefore(dayjs(), "day")) {
      showNotification("Date must be future date", "error");
    }
    if (!!newValue) {
      setSelectedDate((prev) => ({ ...prev, start_date: newValue }));
    }
  };

  const handleEndDateChange = (newValue: Dayjs | null) => {
    if (!newValue) return;
    setSelectedDate((prev) => ({ ...prev, end_date: newValue }));
  };
  const handleBenefits = (event: SelectChangeEvent<typeof benefits>) => {
    const {
      target: { value },
    } = event;
    setBenefits(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value,
    );
  };
  // set up react query mutation
  const mutation = useMutation({
    mutationFn: (fieldValues: JobEmployeeProps) =>
      apiCreateEmployee(fieldValues),
    onSuccess: (data) => {
      // Handle error
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
      }
      //  Handle success
      if ("data" in data) {
        showNotification(data?.data?.message ?? "", "success");
        navigate(PATH_EMPLOYMENT.root);
      }
    },
    onError: (error) => {
      console.error("Process failed", error);
      // Handle error
      showNotification("Process failed", "error");
    },
  });

  const handleJobPost = () => {
    let start_date;
    let end_date;
    if (
      dayjs(selectedDate?.start_date).isValid() &&
      dayjs(selectedDate?.start_date).isAfter(dayjs(), "day")
    ) {
      start_date = dayjs(selectedDate?.start_date).toISOString();
    } else {
      console.error("Invalid date provided for application deadline.");
    }

    if (candidate_info?.job_info?.employment_type === "locum") {
      if (dayjs(selectedDate?.end_date).isValid()) {
        end_date = dayjs(selectedDate?.end_date).toISOString();
      } else {
        console.error("Invalid date provided for application deadline.");
      }
    }

    const date =
      candidate_info?.job_info.employment_type != "full time" &&
      candidate_info?.job_info?.job_type === "full time"
        ? `${contractDuration.startDate} - ${contractDuration.endDate}`
        : employeeData.contract_duration;

    const form_data = {
      ...employeeData,
      contract_duration: date,
      start_date: start_date,
      end_date: end_date ?? "",
      benefits: benefits.join(","),
    };

    const validationErrors = validateEmploeeApplication(
      form_data,
      showNotification,
      candidate_info?.job_info?.job_type,
      candidate_info?.job_info?.employment_type,
    );
    if (validationErrors) {
      mutation.mutate(form_data);
    }
  };
  return (
    <Box sx={{}}>
      <Box>
        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <InputLabel sx={{ ...labelSty, mb: 0 }}>Position</InputLabel>
            <InputCustomDisableSize
              placeholder={""}
              disable={true}
              value={employeeData?.position}
              onchange={(e: any) =>
                setEmployeeData((prev) => ({
                  ...prev,
                  position: e.target.value,
                }))
              }
              type={"text"}
              size={"medium"}
              styles={undefined}
            />
          </Stack>
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <InputLabel sx={{ ...labelSty, mb: 0 }}>Job Type</InputLabel>
            <InputCustomDisableSize
              placeholder={""}
              disable={true}
              value={formatTextType(candidate_info?.job_info.job_type)}
              onchange={(e: any) => {}}
              type={"text"}
              size={"medium"}
              styles={undefined}
            />
          </Stack>
        </Stack>
        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 3, gap: 2 }}
        >
          {candidate_info?.job_info.job_type === "temporary" && (
            <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
              <InputLabel sx={{ ...labelSty, mb: 0 }}>
                Contract Duration
              </InputLabel>

              <InputCustomSize
                placeholder={""}
                value={employeeData?.contract_duration}
                onchange={(e: any) => {}}
                type={"text"}
                size={"medium"}
                styles={undefined}
              />
            </Stack>
          )}
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <InputLabel sx={{ ...labelSty, mb: 0 }}>Salary</InputLabel>
            <InputCustomSize
              placeholder={"Kindly input Salary amount"}
              value={employeeData?.salary}
              onchange={(e: any) =>
                setEmployeeData((prev) => ({
                  ...prev,
                  salary: e.target.value,
                }))
              }
              type={"number"}
              size={"medium"}
              styles={undefined}
            />
          </Stack>
        </Stack>

        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <InputLabel sx={{ ...labelSty, mb: 0 }}>Start Date</InputLabel>
            <CustomDatePicker
              label=""
              value={selectedDate?.start_date}
              onChange={handleStartDateChange}
            />
          </Stack>
          {candidate_info?.job_info?.employment_type === "locum" && (
            <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
              <InputLabel sx={{ ...labelSty, mb: 0 }}>End Date</InputLabel>
              <CustomDatePicker
                label=""
                value={selectedDate?.end_date}
                onChange={handleEndDateChange}
              />
            </Stack>
          )}
        </Stack>
        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <InputLabel sx={{ ...labelSty, mb: 0 }}>Employment Type</InputLabel>
            <InputCustomDisableSize
              placeholder={""}
              disable={true}
              value={formatTextType(employeeData?.employment_type)}
              onchange={(e: any) => {}}
              type={"text"}
              size={"medium"}
              styles={undefined}
            />
          </Stack>
        </Stack>

        <Stack
          direction={isMedium ? "column" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 3, gap: 2 }}
        >
          <Stack sx={{ width: isMedium ? "100%" : "49%", mb: 4 }}>
            <SelectInputMultiple
              label={"Benefits"}
              value={benefits}
              disabled={false}
              onChange={handleBenefits}
              options={[
                { value: "", label: "No benefits" },
                { value: "Health insurance", label: "Health insurance " },
                { value: "Commuting benefits", label: "Commuting benefits" },
                { value: "Accommodation", label: "Accommodation" },
                { value: "Paid leave", label: "Paid leave" },
                { value: "Family care", label: "Family care" },
                { value: "Retirement benefit", label: "Retirement benefit" },
              ]}
            />
          </Stack>
          <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
            <InputLabel sx={{ ...labelSty, mb: 0 }}> Note</InputLabel>
            <TextArea
              placeholder={"Add Note"}
              value={employeeData?.note}
              onchange={(e: any) =>
                setEmployeeData((prev) => ({
                  ...prev,
                  note: e.target.value,
                }))
              }
            />
          </Stack>
        </Stack>

        <Stack direction="row" spacing={2} sx={{ margin: "1rem 0" }}>
          <LoadingButton
            variant="contained"
            onClick={handleJobPost}
            type="submit"
            loading={mutation.isPending}
            sx={{
              width: isMedium ? 180 : 250,
              fontWeight: 700,
              borderRadius: 1,
            }}
          >
            Submit
          </LoadingButton>
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={mutation.isPending}
            sx={{
              width: isMedium ? 180 : 250,
              fontWeight: 700,
              borderRadius: 1,
            }}
          >
            Cancel
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default EmployeeForm;
